import { useRef } from "react";
import { Box, Grid } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { IExtendedSearchItem } from "src/api/client/extended-search.api";
import { EmptyState } from "src/components/common/empty-state";
import { SearchResultCard } from "src/components/pages/search/components/card-result/search-result-card";
import useViewportSpy from "src/hooks/dom/use-viewport-spy";
import { useIsMounted } from "src/hooks/use-is-mounted";
import { LoadingSearchResult } from "./components/card-result/loading-search-result";
import { useDelayedSetState } from "./hooks/use-delayed-set-state";
import { ISearch } from "./search.types";
import { useSearchStore } from "./store/search-store";
import { getMappedStore } from "./store/utils";

const SEARCH_RESULT_SECTION_NO_RESULTS_TEST_ID = "search-result-section-no-results";

interface IResultSectionProps extends ISearch {
    cards: IExtendedSearchItem[] | null;
    currency?: string;
    resultCount: number | null;
    isFailed: boolean;
    isLoading: boolean;
    hasOffset: boolean;
}

const MIN_LOADER_DISPLAY_TIME = 400;

export const ResultSection = ({ cards, currency, resultCount, onSearch, isFailed, isLoading, hasOffset }: IResultSectionProps) => {
    const { t } = useTranslation("extended-search");
    const searchStore = useSearchStore();
    const isMounted = useIsMounted();
    const shouldShowLoader = isMounted && isLoading && !hasOffset && cards !== null;
    const showLoadingSkeleton = useDelayedSetState(shouldShowLoader, MIN_LOADER_DISPLAY_TIME) || !cards;
    const ref = useRef(null);

    const canLoadMore = resultCount !== null && Boolean(resultCount - (cards?.length || 0));

    const onIntersecting = (isIntersecting: boolean) => {
        if (isIntersecting && !isLoading && canLoadMore) {
            onSearch({ ...getMappedStore(searchStore), offset: cards?.length });
        }
    };

    useViewportSpy(ref, { threshold: [0.1, 0.5, 0.7], onIntersecting });

    if (isFailed) {
        return <EmptyState title={t("search-results.search-error-title")} description={t("search-results.search-error-description")} />;
    }

    if (cards && cards.length === 0 && !showLoadingSkeleton) {
        return (
            <EmptyState
                title={t("search-results.no-search-results-title")}
                description={t("search-results.no-search-results-description")}
                data-test-id={SEARCH_RESULT_SECTION_NO_RESULTS_TEST_ID}
            />
        );
    }

    return (
        <Box position="relative" zIndex={0}>
            <Grid templateColumns={["minmax(0, 1fr)", "repeat(2,  minmax(0, 1fr))", "repeat(3,  minmax(0, 1fr))"]} gap="2">
                {showLoadingSkeleton ? (
                    <LoadingSearchResult />
                ) : (
                    cards?.map(({ wikifolioId, symbol, wikifolioShortDescription, trader, rankings, isOnWatchlist, isInvestable }) => (
                        <SearchResultCard
                            key={wikifolioId + symbol}
                            currency={currency}
                            wikifolioId={wikifolioId}
                            symbol={symbol}
                            wikifolioShortDescription={wikifolioShortDescription}
                            trader={trader}
                            rankings={rankings}
                            isOnWatchlist={isOnWatchlist}
                            isInvestable={isInvestable}
                        />
                    ))
                )}
            </Grid>
            <Box
                position="absolute"
                display={showLoadingSkeleton || !canLoadMore ? "none" : "initial"}
                bottom={-5}
                left={0}
                right={0}
                zIndex={-1}
                height={["2200px", "1800px"]}
                ref={ref}
            ></Box>
        </Box>
    );
};
